import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { uploadWithToast } from "@/lib/queuedIngest";
import {
    ClipboardList, Upload, Trash2, FileSpreadsheet, X, ShieldCheck, ShieldAlert,
    FlaskConical, RefreshCw, AlertTriangle, Filter, Stethoscope, Copy, Download,
    BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import ClientPicker from "@/components/ClientPicker";
import DocumentIdInput, { emptyDocSource } from "@/components/DocumentIdInput";

const STATUS_BADGE = {
    active: "bg-emerald-400/10 border-emerald-400/30 text-emerald-400",
    draft: "bg-white/5 border-white/20 text-white/70",
    superseded: "bg-amber-400/10 border-amber-400/30 text-amber-400",
};

const TABS = [
    { key: "overview", label: "Overview" },
    { key: "irb", label: "IRB" },
    { key: "criteria", label: "Inclusion / Exclusion" },
    { key: "cohorts", label: "Cohorts" },
    { key: "meds", label: "Concomitant Meds" },
    { key: "objectives", label: "Objectives & Endpoints" },
    { key: "schedule", label: "Schedule of Events" },
    { key: "visits", label: "Visit Schedule" },
];

export default function ProtocolsPage({ qaMode = false }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [filters, setFilters] = useState({
        is_test_protocol: qaMode ? "true" : "",
        irb_approved: "",
    });

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => { if (v !== "") params.set(k, v); });
            if (qaMode) params.set("is_test_protocol", "true"); // force in QA
            const { data } = await api.get(`/admin/protocols?${params.toString()}`);
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [filters.is_test_protocol, filters.irb_approved]);

    const remove = async (p) => {
        const reason = prompt(`Reason for deleting ${p.protocol_number} v${p.version}?`);
        if (!reason || !reason.trim()) return;
        try {
            await api.delete(`/admin/protocols/${p.id}?reason=${encodeURIComponent(reason)}`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    // When the synchronous ingest hits the 60s ingress timeout but the backend
    // is still processing, the IngestModal calls onDone({poll:true}). We
    // re-fetch the list every 8s for up to 4 minutes so the new protocol shows
    // up automatically.
    const handleIngestDone = (opts = {}) => {
        setOpen(false);
        load();
        if (opts.poll) {
            const initial = items.length;
            let ticks = 0;
            const id = setInterval(async () => {
                ticks += 1;
                try {
                    const params = new URLSearchParams();
                    Object.entries(filters).forEach(([k, v]) => { if (v !== "") params.set(k, v); });
                    const { data } = await api.get(`/admin/protocols?${params.toString()}`);
                    setItems(data.items || []);
                    if ((data.items || []).length > initial) {
                        toast.success("Protocol extracted and saved.");
                        clearInterval(id);
                    }
                } catch (_err) { /* ignore — list view will refresh on next manual reload */ }
                if (ticks >= 30) clearInterval(id);
            }, 8000);
        }
    };

    return (
        <div className="px-8 py-8" data-testid="protocols-page">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED]">{qaMode ? "Quality Assurance" : "Clinical Operations"}</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">{qaMode ? "Protocol Ingestion (QA)" : "Protocols"}</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        {qaMode ? (
                            <>Upload protocols into the SiteCentric test scope for AI training and QA flows.
                            All uploads are tagged <span className="font-mono text-[#c7a6ff]">is_test_protocol=true</span> and stored
                            under the <span className="font-mono">_training</span> sentinel scope. Production protocols
                            ingest only via the published <span className="font-mono">protocol-ingest</span> API flow.</>
                        ) : (
                            <>Upload protocol documents and AIService extracts the full intelligence
                            layer — title, sponsor, IRB approval, inclusion / exclusion criteria, cohorts,
                            concomitant medications, objectives, endpoints, and the visit schedule. Protocols
                            without IRB approval are blocked from clinical-decision use.</>
                        )}
                    </p>
                </div>
                <button onClick={() => setOpen(true)} data-testid="protocol-ingest-btn"
                    className="bg-[#7C3AED] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 flex items-center gap-2">
                    <Upload className="w-4 h-4" strokeWidth={1.8} /> Ingest protocol
                </button>
            </div>

            {/* Filters */}
            {!qaMode && (
            <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-3 mb-3 flex items-center gap-3" data-testid="protocols-filters">
                <Filter className="w-3.5 h-3.5 text-white/40" />
                <select value={filters.is_test_protocol} onChange={(e) => setFilters((p) => ({ ...p, is_test_protocol: e.target.value }))}
                    data-testid="protocols-filter-test"
                    className="bg-[#050505] border border-white/10 rounded-md px-3 py-1.5 text-xs font-mono">
                    <option value="">All protocols</option>
                    <option value="false">Production only</option>
                    <option value="true">AI training only</option>
                </select>
                <select value={filters.irb_approved} onChange={(e) => setFilters((p) => ({ ...p, irb_approved: e.target.value }))}
                    data-testid="protocols-filter-irb"
                    className="bg-[#050505] border border-white/10 rounded-md px-3 py-1.5 text-xs font-mono">
                    <option value="">Any IRB status</option>
                    <option value="true">IRB approved</option>
                    <option value="false">NOT IRB approved</option>
                </select>
                <div className="ml-auto text-[11px] font-mono text-white/45">{items.length} protocol(s)</div>
            </div>
            )}
            {qaMode && (
            <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-3 mb-3 flex items-center gap-3" data-testid="protocols-qa-banner">
                <FlaskConical className="w-3.5 h-3.5 text-[#c7a6ff]" />
                <div className="text-[11px] font-mono text-white/55">Showing test protocols only · sentinel scope <span className="text-[#c7a6ff]">_training</span></div>
                <div className="ml-auto text-[11px] font-mono text-white/45">{items.length} protocol(s)</div>
            </div>
            )}

            <div className="bg-[#0a0a0a] border border-white/10 rounded-md overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="text-left">
                            <th className="label-xs px-5 py-3">Protocol</th>
                            <th className="label-xs px-5 py-3">Sponsor</th>
                            <th className="label-xs px-5 py-3">Version</th>
                            <th className="label-xs px-5 py-3">Compliance</th>
                            <th className="label-xs px-5 py-3">Status</th>
                            <th className="label-xs px-5 py-3">Visits</th>
                            <th className="label-xs px-5 py-3">Updated</th>
                            <th className="label-xs px-5 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="px-5 py-8 text-center text-xs text-white/40 font-mono">Loading…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={8} className="px-5 py-16 text-center">
                                <ClipboardList className="w-10 h-10 text-white/20 mx-auto mb-3" strokeWidth={1.2} />
                                <div className="text-sm text-white/60">No protocols yet</div>
                            </td></tr>
                        ) : items.map((p, idx) => (
                            <tr key={p.id} className="data-row border-t border-white/5 cursor-pointer hover:bg-white/[0.02]" onClick={() => setDetail(p)} data-testid={`protocol-row-${idx}`}>
                                <td className="px-5 py-3">
                                    <div className="flex items-center gap-1.5">
                                        <div className="text-sm font-mono">{p.protocol_number}</div>
                                        {p.is_test_protocol && (
                                            <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider border border-[#7C3AED]/40 bg-[#7C3AED]/15 text-[#c7a6ff] rounded-sm flex items-center gap-1" data-testid={`protocol-badge-test-${idx}`}>
                                                <FlaskConical className="w-2.5 h-2.5" /> training
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-white/50 max-w-xs truncate">{p.title || "—"}</div>
                                </td>
                                <td className="px-5 py-3 text-sm">{p.sponsor || <span className="text-white/30">—</span>}</td>
                                <td className="px-5 py-3 font-mono text-xs">{p.version}</td>
                                <td className="px-5 py-3">
                                    <ComplianceBadge protocol={p} testId={`protocol-irb-${idx}`} />
                                </td>
                                <td className="px-5 py-3">
                                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${STATUS_BADGE[p.status] || STATUS_BADGE.draft}`}>{p.status}</span>
                                </td>
                                <td className="px-5 py-3 font-mono text-sm text-[#7C3AED]">{(p.visits || []).length}</td>
                                <td className="px-5 py-3 font-mono text-xs text-white/60">{p.updated_at ? new Date(p.updated_at).toLocaleString() : "—"}</td>
                                <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                    <button onClick={() => remove(p)} className="p-1.5 rounded hover:bg-red-500/20 text-white/70 hover:text-red-400" data-testid={`protocol-delete-${idx}`}>
                                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {open && <IngestModal qaMode={qaMode} onClose={() => setOpen(false)} onDone={handleIngestDone} />}
            {detail && <DetailModal protocol={detail} onClose={() => setDetail(null)} onChanged={(p) => { setDetail(p); load(); }} />}
        </div>
    );
}

function ComplianceBadge({ protocol, testId }) {
    if (protocol.is_test_protocol) {
        return (
            <span data-testid={testId}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-[#7C3AED]/40 bg-[#7C3AED]/15 text-[#c7a6ff] rounded-sm">
                <FlaskConical className="w-3 h-3" /> training only
            </span>
        );
    }
    if (protocol.irb_approved) {
        return (
            <span data-testid={testId}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-emerald-300/40 bg-emerald-300/10 text-emerald-300 rounded-sm">
                <ShieldCheck className="w-3 h-3" /> IRB approved
            </span>
        );
    }
    return (
        <span data-testid={testId}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-red-300/40 bg-red-300/10 text-red-300 rounded-sm">
            <ShieldAlert className="w-3 h-3" /> NOT IRB approved
        </span>
    );
}

function IngestModal({ qaMode = false, onClose, onDone }) {
    const [companyId, setCompanyId] = useState("");
    const [siteId, setSiteId] = useState("");
    const [sponsor, setSponsor] = useState("");
    const [protocolNumber, setProtocolNumber] = useState("");
    const [version, setVersion] = useState("");
    const [isTest, setIsTest] = useState(qaMode);
    // ── Optional IRB override block (collapsed by default — back-compat) ──
    const [irbOpen, setIrbOpen] = useState(false);
    const [irbApproved, setIrbApproved] = useState(true);
    const [irbBoard, setIrbBoard] = useState("");
    const [irbApprovalId, setIrbApprovalId] = useState("");
    const [irbApprovalDate, setIrbApprovalDate] = useState("");
    const [irbExpirationDate, setIrbExpirationDate] = useState("");
    const [irbExcerpt, setIrbExcerpt] = useState("");
    const [source, setSource] = useState(emptyDocSource("file"));   // multi-file when mode='file'
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const fromS3 = source.mode === "document_id";
        const fl = source.files || [];
        const docId = (source.documentId || "").trim();

        if (!fromS3 && (!fl || !fl.length)) { toast.error("Select at least one file"); return; }
        if (fromS3 && !docId) { toast.error("document_id required"); return; }
        if (!isTest && (!companyId.trim() || !siteId.trim())) {
            toast.error("Company ID and Site ID are required (unless this is a test protocol)");
            return;
        }
        setLoading(true);
        const fd = new FormData();
        if (fromS3) {
            fd.append("document_id", docId);
        } else {
            for (const f of fl) fd.append("files", f);
        }
        if (companyId) fd.append("company_id", companyId);
        if (siteId) fd.append("site_id", siteId);
        if (sponsor) fd.append("sponsor", sponsor);
        if (protocolNumber) fd.append("protocol_number", protocolNumber);
        if (version) fd.append("version", version);
        fd.append("is_test_protocol", isTest ? "true" : "false");
        // Optional IRB override — only attach when admin explicitly opened the block.
        if (irbOpen) {
            fd.append("irb_approved", irbApproved ? "true" : "false");
            if (irbBoard) fd.append("irb_approval_board", irbBoard);
            if (irbApprovalId) fd.append("irb_approval_id", irbApprovalId);
            if (irbApprovalDate) fd.append("irb_approval_date", irbApprovalDate);
            if (irbExpirationDate) fd.append("irb_expiration_date", irbExpirationDate);
            if (irbExcerpt) fd.append("irb_approval_excerpt", irbExcerpt);
        }

        // The ingest queue accepts BOTH multi-file (worker bundles primary
        // + supplements) AND single document_id paths. The queue flag also
        // bypasses the 60s ingress timeout — required for big protocols.
        fd.append("queued", "true");
        const label = fromS3
            ? docId
            : (fl.length > 1
                ? `${fl[0].name} (+${fl.length - 1} supplement${fl.length - 1 === 1 ? "" : "s"})`
                : fl[0].name);
        try {
            await uploadWithToast(api, "/admin/protocols/ingest", fd, {
                filename: label,
                onDone: () => onDone(),
            });
        } catch {
            // uploadWithToast already toasted; refresh list anyway
            // (the protocol record may have been partially created).
            onDone({ poll: true });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal title={qaMode ? "Ingest test protocol (QA)" : "Ingest protocol"} onClose={onClose} testId="protocol-ingest-modal">
            <form onSubmit={submit} className="space-y-3">
                {qaMode ? (
                    <div className="p-3 rounded-md border border-[#7C3AED]/30 bg-[#7C3AED]/5 text-xs text-white/70 flex items-start gap-2.5" data-testid="qa-banner">
                        <FlaskConical className="w-4 h-4 text-[#c7a6ff] mt-0.5 shrink-0" />
                        <div>
                            All QA uploads are filed under the <code className="text-[#c7a6ff]">_training/_training</code> sentinel scope and tagged
                            <code className="text-[#c7a6ff] mx-1">is_test_protocol=true</code>. Every submission — primary protocol + any supplements
                            uploaded together — is processed through the asynchronous Ingest Job Queue (visible in Monitoring → Ingest Job Manager).
                            Production ingestion runs through the published <code className="text-[#c7a6ff] mx-1">protocol-ingest</code> API only.
                        </div>
                    </div>
                ) : (
                <label className="flex items-start gap-2.5 p-3 rounded-md border border-[#7C3AED]/30 bg-[#7C3AED]/5 cursor-pointer hover:bg-[#7C3AED]/10 transition-colors" data-testid="ing-test-toggle">
                    <input type="checkbox" checked={isTest} onChange={(e) => setIsTest(e.target.checked)}
                        className="mt-0.5 accent-[#7C3AED]" data-testid="ing-test-checkbox" />
                    <div>
                        <div className="text-sm font-medium text-[#c7a6ff] flex items-center gap-1.5">
                            <FlaskConical className="w-3.5 h-3.5" /> Mark as test protocol (AI training)
                        </div>
                        <div className="text-[11px] text-white/55 mt-1">
                            Use this when the protocol is for the AI agent training corpus only.
                            Company ID, Site ID, and Protocol Number become optional and will be
                            auto-extracted from the document. The protocol is filed under the
                            <code className="text-[#c7a6ff] mx-1">_training/_training</code>
                            sentinel scope and is excluded from clinical workflows.
                        </div>
                    </div>
                </label>
                )}

                {!qaMode && (
                <div className="grid grid-cols-2 gap-3">
                    <ClientPicker
                        value={companyId} onChange={setCompanyId}
                        label="Company / Client" required={!isTest}
                        excludeDisabled testId="ing-company"
                    />
                    <Input
                        label="Site ID"
                        value={siteId}
                        on={setSiteId}
                        testId="ing-site"
                        required={!isTest}
                        disabled={isTest}
                        hint={isTest ? "auto: _training" : undefined}
                    />
                </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                    <Input
                        label="Sponsor"
                        value={sponsor}
                        on={setSponsor}
                        testId="ing-sponsor"
                        hint="Auto-extracted from document if blank"
                    />
                    <Input
                        label="Protocol number"
                        value={protocolNumber}
                        on={setProtocolNumber}
                        testId="ing-protno"
                        required={false}
                        hint="Auto-extracted from document if blank"
                    />
                </div>
                <Input label="Version" value={version} on={setVersion} testId="ing-version" hint="Auto-extracted from document if blank" />
                <DocumentIdInput
                    value={source}
                    onChange={setSource}
                    accept=".pdf,.docx,.txt,.doc"
                    testId="ing"
                    label="Protocol document(s)"
                    multiple
                />
                <div className="text-[11px] text-white/40 -mt-1">
                    PDF / DOCX / TXT — Claude Sonnet 5.5 will extract title, IRB approval, sponsor,
                    inclusion/exclusion, cohorts, meds, objectives, endpoints, and the visit schedule.
                </div>

                {/* Optional IRB-approval override — collapsed by default so the ingest flow remains backwards-compatible. */}
                <div className="border border-white/10 rounded-md">
                    <button type="button" onClick={() => setIrbOpen(!irbOpen)}
                        data-testid="ing-irb-toggle"
                        className="w-full px-3 py-2 flex items-center justify-between text-xs text-white/70 hover:text-white hover:bg-white/[0.03]">
                        <span className="flex items-center gap-1.5">
                            <ShieldCheck className="w-3.5 h-3.5" />
                            Override IRB approval (optional)
                        </span>
                        <span className="text-[10px] text-white/40 font-mono">{irbOpen ? "−" : "+"}</span>
                    </button>
                    {irbOpen && (
                        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-white/10">
                            <div className="text-[11px] text-white/45">
                                When supplied, these values <strong>override</strong> the AI extractor's IRB block.
                                You'll be auto-stamped as <code className="text-[#c7a6ff]">set_by_email</code> with the current timestamp for audit traceability.
                            </div>
                            <label className="flex items-center gap-2 text-xs cursor-pointer">
                                <input type="checkbox" checked={irbApproved} onChange={(e) => setIrbApproved(e.target.checked)}
                                    data-testid="ing-irb-approved-toggle"
                                    className="w-3.5 h-3.5 accent-emerald-400" />
                                <span className={irbApproved ? "text-emerald-200" : "text-red-200"}>
                                    {irbApproved ? "IRB approved" : "NOT IRB approved"}
                                </span>
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="IRB board" value={irbBoard} onChange={setIrbBoard}
                                    placeholder="e.g. Western IRB" testid="ing-irb-board" />
                                <Field label="Approval ID" value={irbApprovalId} onChange={setIrbApprovalId}
                                    placeholder="e.g. WIRB-20260501-A" testid="ing-irb-approval-id" />
                                <Field label="Approval date" type="date" value={irbApprovalDate} onChange={setIrbApprovalDate} testid="ing-irb-approval-date" />
                                <Field label="Expiration date" type="date" value={irbExpirationDate} onChange={setIrbExpirationDate} testid="ing-irb-expiration-date" />
                            </div>
                            <div>
                                <label className="label-xs mb-1 block">Verbatim excerpt</label>
                                <textarea value={irbExcerpt} onChange={(e) => setIrbExcerpt(e.target.value)} rows={2}
                                    data-testid="ing-irb-excerpt"
                                    placeholder="Optional. Quote 1-3 sentences directly from the approval letter."
                                    className="w-full px-3 py-1.5 rounded-md bg-black/40 border border-white/15 text-xs font-mono text-white/85 focus:outline-none focus:border-white/35 resize-none" />
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">Cancel</button>
                    <button type="submit" disabled={loading} data-testid="ing-submit" className="px-4 py-2 bg-[#7C3AED] text-white rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 disabled:opacity-40">
                        {loading ? "Extracting…" : "Ingest & extract intelligence"}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

function DetailModal({ protocol, onClose, onChanged }) {
    const [tab, setTab] = useState("overview");
    const [reExtracting, setReExtracting] = useState(false);
    const [prescreenOpen, setPrescreenOpen] = useState(false);
    const [synopsisOpen, setSynopsisOpen] = useState(false);

    const reExtract = async () => {
        if (!window.confirm(`Re-run intelligence extractor on ${protocol.protocol_number}? This will rebuild criteria, cohorts, IRB record, and datalake intelligence rows.`)) return;
        setReExtracting(true);
        try {
            const { data } = await api.post(`/admin/protocols/${protocol.id}/re-extract`);
            toast.success("Intelligence re-extracted");
            onChanged(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setReExtracting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div className="w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 rounded-md" onClick={(e) => e.stopPropagation()} data-testid="protocol-detail-modal">
                <div className="px-5 py-4 border-b border-white/10 flex items-center sticky top-0 bg-[#0a0a0a] z-10">
                    <FileSpreadsheet className="w-4 h-4 text-[#7C3AED] mr-2" strokeWidth={1.8} />
                    <div>
                        <div className="font-display text-lg tracking-tight flex items-center gap-2">
                            {protocol.protocol_number} <span className="text-white/40 text-sm">v{protocol.version}</span>
                            <ComplianceBadge protocol={protocol} testId="detail-compliance-badge" />
                        </div>
                        <div className="text-[11px] text-white/50">{protocol.title || ""}</div>
                    </div>
                    <button onClick={reExtract} disabled={reExtracting} data-testid="protocol-re-extract"
                        className="ml-auto mr-2 px-3 py-1.5 border border-[#7C3AED]/40 bg-[#7C3AED]/10 hover:bg-[#7C3AED]/20 text-[#c7a6ff] rounded-md text-xs flex items-center gap-1.5 disabled:opacity-40">
                        <RefreshCw className={`w-3.5 h-3.5 ${reExtracting ? "animate-spin" : ""}`} /> Re-extract intelligence
                    </button>
                    <button onClick={() => setPrescreenOpen(true)} data-testid="protocol-prescreen-open"
                        className="mr-2 px-3 py-1.5 border border-emerald-300/40 bg-emerald-300/10 hover:bg-emerald-300/20 text-emerald-300 rounded-md text-xs flex items-center gap-1.5">
                        <Stethoscope className="w-3.5 h-3.5" /> Prescreening questionnaire
                    </button>
                    <button onClick={() => setSynopsisOpen(true)} data-testid="protocol-synopsis-open"
                        className="mr-2 px-3 py-1.5 border border-sky-300/40 bg-sky-300/10 hover:bg-sky-300/20 text-sky-300 rounded-md text-xs flex items-center gap-1.5">
                        <BookOpen className="w-3.5 h-3.5" /> Analyst synopsis
                    </button>
                    <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
                </div>

                {/* Compliance lock banner */}
                {protocol.compliance_lock_reason && !protocol.is_test_protocol && (
                    <div className="mx-5 mt-4 p-3 border border-red-300/30 bg-red-300/5 rounded-md flex items-start gap-2" data-testid="detail-compliance-lock">
                        <AlertTriangle className="w-4 h-4 mt-0.5 text-red-300 shrink-0" />
                        <div className="text-xs text-red-200">
                            <div className="font-medium">Compliance locked — clinical use blocked</div>
                            <div className="text-white/60 mt-1">{protocol.compliance_lock_reason}</div>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="px-5 pt-4 border-b border-white/10 flex gap-1 overflow-x-auto" data-testid="detail-tabs">
                    {TABS.map((t) => (
                        <button key={t.key} onClick={() => setTab(t.key)} data-testid={`detail-tab-${t.key}`}
                            className={`px-3 py-2 text-xs whitespace-nowrap border-b-2 ${
                                tab === t.key ? "border-[#7C3AED] text-white" : "border-transparent text-white/55 hover:text-white/80"
                            }`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="p-5">
                    {tab === "overview" && <OverviewTab protocol={protocol} />}
                    {tab === "irb" && <IrbTab protocol={protocol} onChanged={onChanged} />}
                    {tab === "criteria" && <CriteriaTab protocol={protocol} />}
                    {tab === "cohorts" && <CohortsTab protocol={protocol} />}
                    {tab === "meds" && <MedsTab protocol={protocol} />}
                    {tab === "objectives" && <ObjectivesTab protocol={protocol} />}
                    {tab === "schedule" && <ScheduleTab protocol={protocol} />}
                    {tab === "visits" && <VisitsTab protocol={protocol} />}
                </div>
            </div>
            {prescreenOpen && (
                <PrescreeningModal protocol={protocol} onClose={() => setPrescreenOpen(false)} />
            )}
            {synopsisOpen && (
                <SynopsisModal protocol={protocol} onClose={() => setSynopsisOpen(false)} />
            )}
        </div>
    );
}

function PrescreeningModal({ protocol, onClose }) {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [tab, setTab] = useState("clinician");
    const [error, setError] = useState(null);

    const generate = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.post("/admin/study-analysis/prescreening-questionnaire", {
                client_id: protocol.company_id,
                protocol_number: protocol.protocol_number,
            });
            setResult(data);
        } catch (e) {
            const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Failed to generate questionnaire";
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { generate(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const items = result ? (tab === "clinician" ? result.clinician_version : result.patient_version) : [];

    const copyText = () => {
        if (!result) return;
        const lines = items.map((it, i) => {
            const head = `${i + 1}. [${it.type.toUpperCase()}] ${it.question}`;
            const rationale = tab === "patient" && it.rationale ? `\n   Why we ask: ${it.rationale}` : "";
            const expected = `\n   Expected answer: ${it.expected_answer}`;
            return head + rationale + expected;
        });
        const header = `${protocol.protocol_number} v${result.from_version} — ${tab === "clinician" ? "Clinician" : "Patient"} prescreening questionnaire`;
        navigator.clipboard.writeText(`${header}\n\n${lines.join("\n\n")}`);
        toast.success("Copied to clipboard");
    };

    const downloadJson = () => {
        if (!result) return;
        const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${protocol.protocol_number}_v${result.from_version}_prescreening.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose}>
            <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 rounded-md" onClick={(e) => e.stopPropagation()} data-testid="prescreen-modal">
                <div className="px-5 py-4 border-b border-white/10 flex items-center sticky top-0 bg-[#0a0a0a] z-10">
                    <Stethoscope className="w-4 h-4 text-emerald-300 mr-2" />
                    <div>
                        <div className="font-display text-lg tracking-tight">Prescreening Questionnaire</div>
                        <div className="text-[11px] text-white/50">
                            {protocol.protocol_number}
                            {result?.from_version ? <> v{result.from_version} <span className="text-white/30">(latest IRB-approved)</span></> : null}
                        </div>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        {result && (
                            <>
                                <button onClick={copyText} data-testid="prescreen-copy"
                                    className="px-3 py-1.5 border border-white/15 hover:bg-white/5 rounded-md text-xs flex items-center gap-1.5">
                                    <Copy className="w-3.5 h-3.5" /> Copy
                                </button>
                                <button onClick={downloadJson} data-testid="prescreen-download"
                                    className="px-3 py-1.5 border border-white/15 hover:bg-white/5 rounded-md text-xs flex items-center gap-1.5">
                                    <Download className="w-3.5 h-3.5" /> JSON
                                </button>
                                <button onClick={generate} disabled={loading} data-testid="prescreen-regen"
                                    className="px-3 py-1.5 border border-[#7C3AED]/40 bg-[#7C3AED]/10 text-[#c7a6ff] rounded-md text-xs flex items-center gap-1.5 disabled:opacity-40">
                                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Regenerate
                                </button>
                            </>
                        )}
                        <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
                    </div>
                </div>

                {loading && (
                    <div className="p-10 text-center text-sm text-white/60" data-testid="prescreen-loading">
                        <RefreshCw className="w-5 h-5 mx-auto mb-3 animate-spin text-[#7C3AED]" />
                        Generating both versions from inclusion / exclusion criteria…
                    </div>
                )}

                {error && !loading && (
                    <div className="p-6 text-sm text-red-300" data-testid="prescreen-error">
                        <AlertTriangle className="w-4 h-4 inline mr-1" />{error}
                    </div>
                )}

                {result && !loading && (
                    <>
                        <div className="px-5 pt-4 border-b border-white/10 flex gap-1">
                            <button onClick={() => setTab("clinician")} data-testid="prescreen-tab-clinician"
                                className={`px-3 py-2 text-xs border-b-2 ${tab === "clinician" ? "border-emerald-300 text-white" : "border-transparent text-white/55 hover:text-white/80"}`}>
                                Clinician (verbatim)
                            </button>
                            <button onClick={() => setTab("patient")} data-testid="prescreen-tab-patient"
                                className={`px-3 py-2 text-xs border-b-2 ${tab === "patient" ? "border-emerald-300 text-white" : "border-transparent text-white/55 hover:text-white/80"}`}>
                                Patient (plain language)
                            </button>
                            <div className="ml-auto text-[11px] text-white/40 self-center">
                                {result.counts?.inclusion ?? 0} inclusion · {result.counts?.exclusion ?? 0} exclusion · {result.counts?.total ?? 0} total
                            </div>
                        </div>
                        <div className="p-5 space-y-3" data-testid={`prescreen-list-${tab}`}>
                            {items.length === 0 && <div className="text-xs text-white/50">No questions generated.</div>}
                            {items.map((it, idx) => (
                                <div key={it.id || idx} data-testid={`prescreen-item-${idx}`}
                                    className={`p-3 rounded-md border ${it.type === "inclusion"
                                        ? "border-emerald-300/20 bg-emerald-300/5"
                                        : "border-amber-300/20 bg-amber-300/5"}`}>
                                    <div className="flex items-start gap-2">
                                        <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded-sm ${it.type === "inclusion" ? "bg-emerald-300/20 text-emerald-300" : "bg-amber-300/20 text-amber-300"}`}>
                                            {it.type}
                                        </span>
                                        <span className="text-[10px] text-white/40 font-mono mt-0.5">{it.id}</span>
                                        <span className="ml-auto text-[10px] text-white/40">expects: <span className="font-mono">{it.expected_answer}</span></span>
                                    </div>
                                    <div className="text-sm mt-2">{it.question}</div>
                                    {tab === "patient" && it.rationale && (
                                        <div className="text-[11px] text-white/50 mt-1.5 italic">Why we ask: {it.rationale}</div>
                                    )}
                                    {it.source_criterion && (
                                        <div className="text-[11px] text-white/40 mt-2 pt-2 border-t border-white/10">
                                            Source criterion: <span className="text-white/55">{it.source_criterion}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function SynopsisModal({ protocol, onClose }) {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [sitesText, setSitesText] = useState("");
    const [nctId, setNctId] = useState("");

    const generate = async () => {
        setLoading(true);
        setError(null);
        try {
            const sites = sitesText
                .split(/[\n,]/)
                .map((s) => s.trim())
                .filter(Boolean);
            const { data } = await api.post("/admin/study-analysis/protocol-synopsis", {
                client_id: protocol.company_id,
                protocol_number: protocol.protocol_number,
                sites,
                nct_id: nctId.trim() || null,
            });
            setResult(data);
        } catch (e) {
            const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Failed to generate synopsis";
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const copyMarkdown = () => {
        if (!result?.synopsis_markdown) return;
        navigator.clipboard.writeText(result.synopsis_markdown);
        toast.success("Markdown copied");
    };

    const downloadMarkdown = () => {
        if (!result?.synopsis_markdown) return;
        const blob = new Blob([result.synopsis_markdown], { type: "text/markdown" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${protocol.protocol_number}_v${result.from_version}_synopsis.md`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose}>
            <div className="w-full max-w-5xl max-h-[92vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 rounded-md" onClick={(e) => e.stopPropagation()} data-testid="synopsis-modal">
                <div className="px-5 py-4 border-b border-white/10 flex items-center sticky top-0 bg-[#0a0a0a] z-10">
                    <BookOpen className="w-4 h-4 text-sky-300 mr-2" />
                    <div>
                        <div className="font-display text-lg tracking-tight">Protocol Synopsis</div>
                        <div className="text-[11px] text-white/50">
                            {protocol.protocol_number}
                            {result?.from_version ? <> v{result.from_version} <span className="text-white/30">(latest IRB-approved)</span></> : null}
                            {result?.nct_id ? <span className="ml-2 text-sky-300/70 font-mono">{result.nct_id}</span> : null}
                            {result?.ctgov_used === false && result?.nct_id && (
                                <span className="ml-2 text-amber-300/70">(ClinicalTrials.gov fetch failed)</span>
                            )}
                        </div>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        {result?.synopsis_markdown && (
                            <>
                                <button onClick={copyMarkdown} data-testid="synopsis-copy"
                                    className="px-3 py-1.5 border border-white/15 hover:bg-white/5 rounded-md text-xs flex items-center gap-1.5">
                                    <Copy className="w-3.5 h-3.5" /> Copy
                                </button>
                                <button onClick={downloadMarkdown} data-testid="synopsis-download"
                                    className="px-3 py-1.5 border border-white/15 hover:bg-white/5 rounded-md text-xs flex items-center gap-1.5">
                                    <Download className="w-3.5 h-3.5" /> .md
                                </button>
                            </>
                        )}
                        <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
                    </div>
                </div>

                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="text-xs text-white/50 mb-1">Candidate sites <span className="text-white/30">(comma or newline separated, optional)</span></div>
                            <textarea value={sitesText} onChange={(e) => setSitesText(e.target.value)} data-testid="synopsis-sites"
                                rows={3} placeholder="e.g. Mayo Clinic Rochester MN, Boston Medical Center, UCLA, Cleveland Clinic"
                                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-xs text-white placeholder-white/30" />
                        </div>
                        <div>
                            <div className="text-xs text-white/50 mb-1">ClinicalTrials.gov NCT id <span className="text-white/30">(optional, auto-detected)</span></div>
                            <input value={nctId} onChange={(e) => setNctId(e.target.value)} data-testid="synopsis-nct"
                                placeholder="NCT04567890"
                                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-xs text-white placeholder-white/30 font-mono" />
                            <button onClick={generate} disabled={loading} data-testid="synopsis-generate"
                                className="mt-2 w-full px-3 py-2 bg-[#7C3AED] hover:bg-[#7C3AED]/90 disabled:opacity-40 text-white rounded-md text-xs font-medium flex items-center justify-center gap-1.5">
                                {loading
                                    ? (<><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Generating synopsis…</>)
                                    : (result ? "Regenerate" : "Generate synopsis")}
                            </button>
                        </div>
                    </div>

                    {error && !loading && (
                        <div className="p-4 border border-red-300/30 bg-red-300/5 text-red-200 text-xs rounded-md flex items-start gap-2" data-testid="synopsis-error">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                            <div>{error}</div>
                        </div>
                    )}

                    {loading && !result && (
                        <div className="p-10 text-center text-sm text-white/60" data-testid="synopsis-loading">
                            <RefreshCw className="w-5 h-5 mx-auto mb-3 animate-spin text-[#7C3AED]" />
                            Drafting full synopsis (compound, design, recruitment, visit math, financial risk)…
                        </div>
                    )}

                    {result?.synopsis_markdown && !loading && (
                        <div className="border border-white/10 rounded-md p-5 bg-white/[0.02]" data-testid="synopsis-content">
                            <pre className="whitespace-pre-wrap text-sm leading-relaxed text-white/90 font-sans">
                                {result.synopsis_markdown}
                            </pre>
                        </div>
                    )}

                    {result && !result.synopsis_markdown && !loading && (
                        <div className="p-4 border border-amber-300/30 bg-amber-300/5 text-amber-200 text-xs rounded-md" data-testid="synopsis-empty">
                            The model returned a response but no <code>&lt;synopsis&gt;</code> block was detected. Try regenerating.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}



function OverviewTab({ protocol }) {
    const pop = protocol.population || {};
    return (
        <div className="space-y-4" data-testid="tab-overview">
            <div className="grid grid-cols-4 gap-3 text-xs">
                <KV k="Sponsor" v={protocol.sponsor} />
                <KV k="Phase" v={protocol.phase} />
                <KV k="Therapeutic area" v={protocol.therapeutic_area} />
                <KV k="Status" v={protocol.status} />
                <KV k="Company" v={protocol.company_id} />
                <KV k="Site" v={protocol.site_id} />
                <KV k="Visits" v={(protocol.visits || []).length} />
                <KV k="Source docs" v={(protocol.source_document_ids || []).length} />
                <KV k="Target N" v={pop.target_n} />
                <KV k="Age range" v={pop.age_range} />
                <KV k="Gender" v={pop.gender} />
                <KV k="Condition" v={pop.condition} />
            </div>
            {(protocol.companions || []).length > 0 && (
                <div className="bg-[#7C3AED]/5 border border-[#7C3AED]/20 rounded-md p-3" data-testid="protocol-companions">
                    <div className="label-xs text-[#7C3AED] mb-2 flex items-center gap-1.5">
                        <FileSpreadsheet className="w-3 h-3" strokeWidth={1.8} />
                        Also tagged for {(protocol.companions || []).length} other scope(s) — same source files, no re-processing
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {(protocol.companions || []).map((c, i) => (
                            <span key={i} className="px-2 py-1 rounded-sm border border-[#7C3AED]/30 bg-[#7C3AED]/10 text-[11px] font-mono text-[#7C3AED]">
                                {c.company_id} / {c.site_id} · {c.protocol_number} v{c.version}
                            </span>
                        ))}
                    </div>
                </div>
            )}
            {protocol.source_signature && (
                <div className="text-[10px] font-mono text-white/30 truncate">
                    source signature: {protocol.source_signature}
                </div>
            )}
        </div>
    );
}

function IrbTab({ protocol, onChanged }) {
    const irb = protocol.irb_approval_details || {};
    const [editing, setEditing] = useState(false);
    return (
        <div className="space-y-3" data-testid="tab-irb">
            <div className={`p-4 rounded-md border ${
                protocol.irb_approved
                    ? "border-emerald-300/30 bg-emerald-300/5"
                    : "border-red-300/30 bg-red-300/5"
            }`}>
                <div className="flex items-center gap-2">
                    {protocol.irb_approved ? <ShieldCheck className="w-4 h-4 text-emerald-300" /> : <ShieldAlert className="w-4 h-4 text-red-300" />}
                    <span className={`font-display text-base ${protocol.irb_approved ? "text-emerald-200" : "text-red-200"}`}>
                        {protocol.irb_approved ? "IRB APPROVED" : "NOT IRB APPROVED"}
                    </span>
                    {protocol.is_test_protocol && (
                        <span className="ml-auto text-[10px] uppercase tracking-wider text-[#c7a6ff] font-mono">flagged as training corpus</span>
                    )}
                    {!editing && (
                        <button
                            type="button"
                            onClick={() => setEditing(true)}
                            data-testid="irb-flag-edit-open"
                            className={`${protocol.is_test_protocol ? "" : "ml-auto"} px-2.5 py-1 rounded-md border border-white/15 text-white/70 hover:text-white hover:bg-white/5 text-[11px] flex items-center gap-1`}
                        >
                            <ShieldCheck className="w-3 h-3" />
                            {protocol.irb_approved ? "Update IRB approval" : "Flag as IRB approved"}
                        </button>
                    )}
                </div>
                {!protocol.irb_approved && !protocol.is_test_protocol && (
                    <div className="text-[11px] text-red-100/80 mt-2">
                        This protocol is BLOCKED from clinical decision-making, patient scheduling,
                        and any production workflow. Agent tools that try to retrieve its schedule
                        will receive an error. Either re-ingest with the IRB approval letter, or
                        toggle <code className="text-[#c7a6ff]">is_test_protocol</code> to use it
                        for AI-agent training only.
                    </div>
                )}
            </div>

            {editing && (
                <IrbFlagEditor
                    protocol={protocol}
                    onCancel={() => setEditing(false)}
                    onSaved={(updated) => {
                        setEditing(false);
                        if (onChanged) onChanged(updated);
                    }}
                />
            )}

            <div className="grid grid-cols-2 gap-3 text-xs">
                <KV k="IRB / Ethics board" v={irb.board} />
                <KV k="Approval ID" v={irb.approval_id} />
                <KV k="Approval date" v={irb.approval_date} />
                <KV k="Expiration date" v={irb.expiration_date} />
            </div>
            {(irb.set_by_email || irb.set_at) && (
                <div className="text-[10px] text-white/40 font-mono pt-1 border-t border-white/5">
                    Last changed by <span className="text-white/65">{irb.set_by_email || "—"}</span>
                    {irb.set_at ? <> on <span className="text-white/65">{new Date(irb.set_at).toLocaleString()}</span></> : null}
                    {irb.source ? <> · via <span className="text-white/55">{irb.source}</span></> : null}
                </div>
            )}
            {irb.raw_excerpt && (
                <div>
                    <div className="label-xs mb-1.5">Verbatim excerpt</div>
                    <pre className="text-xs font-mono whitespace-pre-wrap bg-[#050505] border border-white/10 rounded p-3 text-white/80">{irb.raw_excerpt}</pre>
                </div>
            )}
        </div>
    );
}

function IrbFlagEditor({ protocol, onCancel, onSaved }) {
    const seed = protocol.irb_approval_details || {};
    const currentlyApproved = !!protocol.irb_approved;
    const [board, setBoard] = useState(seed.board || "");
    const [approvalId, setApprovalId] = useState(seed.approval_id || "");
    const [approvalDate, setApprovalDate] = useState(seed.approval_date || "");
    const [expirationDate, setExpirationDate] = useState(seed.expiration_date || "");
    const [rawExcerpt, setRawExcerpt] = useState(seed.raw_excerpt || "");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    // Revoke is a SECOND-STEP confirm flow shown only when the protocol is
    // currently IRB-approved. Until then the editor's primary action is
    // always "Save IRB approval" — there's no toggle to mark something
    // un-approved by accident.
    const [revokeConfirming, setRevokeConfirming] = useState(false);
    const [revokeReason, setRevokeReason] = useState("");

    const persist = async (irbApproved, persistReason) => {
        setSaving(true);
        try {
            const body = {
                irb_approved: irbApproved,
                board: board || null,
                approval_id: approvalId || null,
                approval_date: approvalDate || null,
                expiration_date: expirationDate || null,
                raw_excerpt: rawExcerpt || null,
                reason: persistReason || null,
            };
            const { data } = await api.post(`/admin/protocols/${protocol.id}/irb-flag`, body);
            toast.success(irbApproved
                ? (currentlyApproved ? "IRB approval updated" : "Protocol flagged as IRB-approved")
                : "IRB approval revoked");
            onSaved({
                ...protocol,
                irb_approved: data.irb_approved,
                irb_approval_details: data.irb_approval_details,
                compliance_lock_reason: data.compliance_lock_reason,
                updated_at: data.updated_at,
            });
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const save = () => persist(true, reason);

    const startRevoke = () => {
        setRevokeReason(reason || "");
        setRevokeConfirming(true);
    };

    const confirmRevoke = () => {
        if (!revokeReason.trim()) {
            toast.error("Reason is required to revoke an IRB approval.");
            return;
        }
        persist(false, revokeReason);
    };

    return (
        <div className="p-4 rounded-md border border-[#7C3AED]/30 bg-[#7C3AED]/[0.04] space-y-3" data-testid="irb-flag-editor">
            <div className="flex items-center justify-between">
                <div className="text-sm font-medium tracking-tight">
                    {currentlyApproved ? "Update IRB approval" : "Flag protocol as IRB-approved"}
                </div>
                <button type="button" onClick={onCancel} className="text-white/50 hover:text-white text-[11px]"
                    data-testid="irb-flag-cancel">Cancel</button>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <Field label="IRB / Ethics board" value={board} onChange={setBoard}
                    placeholder="e.g. Western IRB" testid="irb-flag-board" />
                <Field label="Approval ID" value={approvalId} onChange={setApprovalId}
                    placeholder="e.g. WIRB-20260501-A" testid="irb-flag-approval-id" />
                <Field label="Approval date" type="date" value={approvalDate} onChange={setApprovalDate} testid="irb-flag-approval-date" />
                <Field label="Expiration date" type="date" value={expirationDate} onChange={setExpirationDate} testid="irb-flag-expiration-date" />
            </div>
            <div>
                <label className="label-xs mb-1 block">Verbatim excerpt from the approval letter (audit evidence)</label>
                <textarea value={rawExcerpt} onChange={(e) => setRawExcerpt(e.target.value)} rows={3}
                    data-testid="irb-flag-raw-excerpt"
                    placeholder="Optional. Quote 1-3 sentences directly from the letter."
                    className="w-full px-3 py-1.5 rounded-md bg-black/40 border border-white/15 text-xs font-mono text-white/85 focus:outline-none focus:border-white/35 resize-none" />
            </div>
            <div>
                <label className="label-xs mb-1 block">Reason (captured in the audit log)</label>
                <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                    data-testid="irb-flag-reason"
                    placeholder="e.g. Sponsor uploaded the IRB approval letter on YYYY-MM-DD"
                    className="w-full px-3 py-1.5 rounded-md bg-black/40 border border-white/15 text-xs text-white/85 focus:outline-none focus:border-white/35" />
            </div>
            <div className="flex justify-between items-center gap-2 pt-1">
                {/* Revoke is a separate destructive action and only available on protocols
                    that are currently IRB-approved. Two-step confirm with a required reason. */}
                {currentlyApproved && !revokeConfirming && (
                    <button type="button" onClick={startRevoke} disabled={saving}
                        data-testid="irb-flag-revoke-start"
                        className="px-3 py-1.5 rounded-md border border-red-400/40 text-red-200 hover:bg-red-400/10 text-xs font-medium flex items-center gap-1.5">
                        <ShieldAlert className="w-3.5 h-3.5" />
                        Revoke approval…
                    </button>
                )}
                {!currentlyApproved && <div />}
                <button type="button" onClick={save} disabled={saving || revokeConfirming}
                    data-testid="irb-flag-save"
                    className="px-3 py-1.5 rounded-md bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-40 text-xs font-medium flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    {saving && !revokeConfirming
                        ? "Saving…"
                        : (currentlyApproved ? "Save IRB approval" : "Save — flag as IRB approved")}
                </button>
            </div>

            {revokeConfirming && (
                <div className="p-3 rounded-md border border-red-400/40 bg-red-400/[0.06] space-y-2" data-testid="irb-flag-revoke-confirm">
                    <div className="text-[11px] text-red-100 flex items-start gap-1.5">
                        <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <span>
                            Revoking IRB approval will <strong>compliance-lock</strong> this protocol.
                            Clinical workflows (chat, scheduling, form generation) will refuse to
                            use it until it is re-approved. The reason below is recorded in the
                            audit log.
                        </span>
                    </div>
                    <input type="text" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)}
                        autoFocus
                        data-testid="irb-flag-revoke-reason"
                        placeholder="Required — e.g. Approval letter withdrawn by IRB on YYYY-MM-DD"
                        className="w-full px-3 py-1.5 rounded-md bg-black/40 border border-red-400/30 text-xs text-white/85 focus:outline-none focus:border-red-300/60" />
                    <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => setRevokeConfirming(false)} disabled={saving}
                            data-testid="irb-flag-revoke-cancel"
                            className="px-3 py-1.5 rounded-md border border-white/15 text-white/70 hover:text-white hover:bg-white/5 text-xs">
                            Back
                        </button>
                        <button type="button" onClick={confirmRevoke} disabled={saving || !revokeReason.trim()}
                            data-testid="irb-flag-revoke-confirm-btn"
                            className="px-3 py-1.5 rounded-md bg-red-500 hover:bg-red-600 disabled:opacity-40 text-white text-xs font-medium flex items-center gap-1.5">
                            <ShieldAlert className="w-3.5 h-3.5" />
                            {saving ? "Revoking…" : "Revoke approval"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, value, onChange, type = "text", placeholder = "", testid }) {
    return (
        <div>
            <label className="label-xs mb-1 block">{label}</label>
            <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder} data-testid={testid}
                className="w-full px-3 py-1.5 rounded-md bg-black/40 border border-white/15 text-xs text-white/85 focus:outline-none focus:border-white/35" />
        </div>
    );
}

function CriteriaTab({ protocol }) {
    const incl = protocol.inclusion_criteria || [];
    const excl = protocol.exclusion_criteria || [];
    const renderCriterion = (c, i, prefix) => {
        const text = typeof c === "string" ? c : (c?.text || "");
        const category = typeof c === "string" ? null : (c?.category || null);
        return (
            <div key={i} className="text-[11px] text-white/85 p-2 border border-white/5 bg-[#050505] rounded flex items-start gap-2" data-testid={`${prefix}-${i}`}>
                <span className="text-white/40 font-mono shrink-0">{i + 1}.</span>
                <span className="flex-1">{text}</span>
                {category && (
                    <span className="shrink-0 px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider font-mono border border-[#7C3AED]/30 bg-[#7C3AED]/10 text-[#c7a6ff]"
                          data-testid={`${prefix}-${i}-category`}>
                        {category.replace(/_/g, " ")}
                    </span>
                )}
            </div>
        );
    };
    return (
        <div className="grid grid-cols-2 gap-4" data-testid="tab-criteria">
            <div>
                <div className="label-xs mb-2 flex items-center gap-2 text-emerald-300">
                    <ShieldCheck className="w-3 h-3" /> Inclusion ({incl.length})
                </div>
                <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                    {incl.length === 0 ? <div className="text-xs text-white/40 font-mono">No criteria extracted</div> :
                        incl.map((c, i) => renderCriterion(c, i, "incl"))}
                </div>
            </div>
            <div>
                <div className="label-xs mb-2 flex items-center gap-2 text-red-300">
                    <ShieldAlert className="w-3 h-3" /> Exclusion ({excl.length})
                </div>
                <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                    {excl.length === 0 ? <div className="text-xs text-white/40 font-mono">No criteria extracted</div> :
                        excl.map((c, i) => renderCriterion(c, i, "excl"))}
                </div>
            </div>
        </div>
    );
}

function CohortsTab({ protocol }) {
    const cohorts = protocol.cohorts || [];
    return (
        <div data-testid="tab-cohorts">
            {cohorts.length === 0 ? <div className="text-xs text-white/40 font-mono">No cohorts extracted</div> :
                <div className="grid grid-cols-2 gap-3">
                    {cohorts.map((c, i) => (
                        <div key={c.name || `cohort-${i}`} className="p-4 rounded-md border border-white/10 bg-[#050505]" data-testid={`cohort-${i}`}>
                            <div className="font-display text-base text-white">{c.name || `Cohort ${i + 1}`}</div>
                            {c.intervention && <div className="text-[11px] font-mono text-[#c7a6ff] mt-1">{c.intervention}</div>}
                            {c.description && <div className="text-[11px] text-white/70 mt-2">{c.description}</div>}
                            {c.target_n != null && <div className="text-[10px] font-mono text-white/45 mt-2">target n = {c.target_n}</div>}
                        </div>
                    ))}
                </div>}
        </div>
    );
}

function MedsTab({ protocol }) {
    const m = protocol.concomitant_medications || {};
    const cols = [
        { key: "allowed", label: "Allowed", tone: "emerald" },
        { key: "restricted", label: "Restricted", tone: "amber" },
        { key: "prohibited", label: "Prohibited", tone: "red" },
    ];
    return (
        <div className="grid grid-cols-3 gap-4" data-testid="tab-meds">
            {cols.map((col) => {
                const items = m[col.key] || [];
                return (
                    <div key={col.key}>
                        <div className={`label-xs mb-2 text-${col.tone}-300`}>{col.label} ({items.length})</div>
                        <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
                            {items.length === 0 ? <div className="text-xs text-white/40 font-mono">—</div> :
                                items.map((it, i) => (
                                    <div key={`${col.key}-${typeof it === "string" ? it : i}`} className={`text-[11px] p-2 border border-${col.tone}-300/20 bg-${col.tone}-300/5 rounded`} data-testid={`med-${col.key}-${i}`}>
                                        {it}
                                    </div>
                                ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function ObjectivesTab({ protocol }) {
    const obj = protocol.objectives || {};
    const ep = protocol.endpoints || {};
    return (
        <div className="space-y-4" data-testid="tab-objectives">
            <div>
                <div className="label-xs mb-2">Primary objective</div>
                <div className="text-xs text-white/85 p-3 border border-white/10 bg-[#050505] rounded">{obj.primary || "—"}</div>
            </div>
            {(obj.secondary || []).length > 0 && (
                <div>
                    <div className="label-xs mb-2">Secondary objectives</div>
                    <ul className="space-y-1">
                        {(obj.secondary || []).map((s, i) => (
                            <li key={i} className="text-[11px] text-white/80 p-2 border border-white/5 bg-[#050505] rounded">• {s}</li>
                        ))}
                    </ul>
                </div>
            )}
            <div className="grid grid-cols-3 gap-3">
                {["primary", "secondary", "exploratory"].map((kind) => {
                    const items = ep[kind] || [];
                    return (
                        <div key={kind}>
                            <div className="label-xs mb-2 capitalize">{kind} endpoints ({items.length})</div>
                            <div className="space-y-1.5 max-h-[40vh] overflow-y-auto">
                                {items.length === 0 ? <div className="text-xs text-white/40 font-mono">—</div> :
                                    items.map((s, i) => <div key={i} className="text-[11px] text-white/80 p-2 border border-white/5 bg-[#050505] rounded">{s}</div>)}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

const CATEGORY_LABELS = {
    consent: "Consent", demographics: "Demographics", vitals: "Vitals", lab: "Lab",
    ecg: "ECG", imaging: "Imaging", drug_admin: "Drug admin", pk_pd: "PK/PD",
    biopsy: "Biopsy", epro: "ePRO", questionnaire: "Questionnaire",
    ae_review: "AE review", conmed_review: "ConMed review",
    randomization: "Randomization", physical_exam: "Physical exam",
    follow_up: "Follow-up", other: "Other",
    diagnosis: "Diagnosis", disease_severity: "Disease severity",
    lab_value: "Lab value", prior_therapy: "Prior therapy",
    concomitant_meds: "ConMeds", contraception: "Contraception",
    pregnancy: "Pregnancy", comorbidity: "Comorbidity",
    compliance: "Compliance", geographic: "Geographic", safety: "Safety",
};

function CategoryBadge({ category, testId }) {
    if (!category) return null;
    const label = CATEGORY_LABELS[category] || category.replace(/_/g, " ");
    return (
        <span className="px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider font-mono border border-[#7C3AED]/30 bg-[#7C3AED]/10 text-[#c7a6ff]"
              data-testid={testId}>
            {label}
        </span>
    );
}

function ScheduleTab({ protocol }) {
    const events = useMemo(() => protocol.schedule_of_events || [], [protocol]);
    const visits = useMemo(() => protocol.visits || [], [protocol]);
    // Prefer canonical visit ordering when available; fall back to event-derived names.
    const visitNames = useMemo(() => {
        if (visits.length) return visits.map((v) => v.name);
        return Array.from(new Set(events.flatMap((e) => e.visits || [])));
    }, [events, visits]);
    if (events.length === 0) {
        return <div className="text-xs text-white/40 font-mono" data-testid="tab-schedule-empty">No schedule of events extracted</div>;
    }
    return (
        <div className="overflow-x-auto" data-testid="tab-schedule">
            <table className="text-[11px] font-mono">
                <thead>
                    <tr>
                        <th className="px-2 py-1.5 text-left label-xs sticky left-0 bg-[#0a0a0a] z-10">Event</th>
                        <th className="px-2 py-1.5 text-left label-xs">Category</th>
                        {visitNames.map((v) => (
                            <th key={v} className="px-2 py-1.5 label-xs whitespace-nowrap">{v}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {events.map((e, i) => (
                        <tr key={e.event_name || `event-${i}`} className="border-t border-white/5" data-testid={`schedule-row-${i}`}>
                            <td className="px-2 py-1.5 sticky left-0 bg-[#0a0a0a] text-white">{e.event_name}</td>
                            <td className="px-2 py-1.5"><CategoryBadge category={e.category} testId={`schedule-row-${i}-category`} /></td>
                            {visitNames.map((v) => (
                                <td key={v} className="px-2 py-1.5 text-center">
                                    {(e.visits || []).includes(v) ? <span className="text-emerald-300">●</span> : <span className="text-white/15">·</span>}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function VisitsTab({ protocol }) {
    const visits = protocol.visits || [];
    if (visits.length === 0) return <div className="text-xs text-white/40 font-mono" data-testid="tab-visits-empty">No visits extracted</div>;
    return (
        <div className="space-y-3" data-testid="tab-visits">
            {visits.map((v, i) => (
                <div key={v.name || `visit-${i}`} className="bg-[#050505] border border-white/10 rounded-md p-4" data-testid={`visit-row-${i}`}>
                    <div className="flex items-start gap-3 mb-3">
                        <span className="font-mono text-xs text-white/40 mt-1">#{v.sequence || i + 1}</span>
                        <div className="flex-1">
                            <div className="font-display text-base text-white">{v.name}</div>
                            <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px] font-mono text-white/60">
                                {v.timing_label && (
                                    <span className="text-[#c7a6ff]" data-testid={`visit-row-${i}-timing`}>
                                        {v.timing_label}
                                    </span>
                                )}
                                {(v.day_offset_from_baseline !== null && v.day_offset_from_baseline !== undefined) && (
                                    <span className="text-white/45" data-testid={`visit-row-${i}-day`}>
                                        D{v.day_offset_from_baseline >= 0 ? "+" : ""}{v.day_offset_from_baseline}
                                    </span>
                                )}
                                {(v.window_before_days || v.window_after_days) ? (
                                    <span className="text-white/45" data-testid={`visit-row-${i}-window`}>
                                        window: −{v.window_before_days || 0} / +{v.window_after_days || 0} d
                                    </span>
                                ) : null}
                            </div>
                        </div>
                        {(v.forms_required || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 max-w-[40%] justify-end">
                                {(v.forms_required || []).map((f, j) => (
                                    <span key={j} className="px-1.5 py-0.5 rounded-sm bg-[#7C3AED]/10 border border-[#7C3AED]/20 text-[10px] text-[#c7a6ff] font-mono">{f}</span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="label-xs mb-1.5 text-white/55">Procedures ({(v.procedures || []).length})</div>
                            <div className="space-y-1">
                                {(v.procedures || []).length === 0 ? <div className="text-[11px] text-white/30 font-mono">—</div> :
                                    (v.procedures || []).map((p, j) => (
                                        <div key={j} className="text-[11px] text-white/85 px-2 py-1 border border-white/5 bg-[#0a0a0a] rounded" data-testid={`visit-row-${i}-procedure-${j}`}>
                                            {p}
                                        </div>
                                    ))}
                            </div>
                        </div>
                        <div>
                            <div className="label-xs mb-1.5 text-white/55">Assessments ({(v.assessments || []).length})</div>
                            <div className="space-y-1">
                                {(v.assessments || []).length === 0 ? <div className="text-[11px] text-white/30 font-mono">—</div> :
                                    (v.assessments || []).map((a, j) => (
                                        <div key={j} className="text-[11px] text-white/85 px-2 py-1 border border-white/5 bg-[#0a0a0a] rounded" data-testid={`visit-row-${i}-assessment-${j}`}>
                                            {a}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                    {v.notes && (
                        <div className="mt-2 text-[11px] text-white/55 italic">Note: {v.notes}</div>
                    )}
                </div>
            ))}
        </div>
    );
}

function KV({ k, v }) {
    return (
        <div>
            <div className="label-xs mb-0.5">{k}</div>
            <div className="text-sm font-mono text-white/80">{v == null || v === "" ? "—" : String(v)}</div>
        </div>
    );
}

function Input({ label, value, on, required, hint, testId, disabled }) {
    return (
        <div>
            <div className="label-xs mb-1.5">{label}{required && <span className="text-[#7C3AED] ml-1">*</span>}</div>
            <input value={value} required={required} disabled={disabled} onChange={(e) => on(e.target.value)} data-testid={testId}
                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30 disabled:opacity-40" />
            {hint && <div className="text-[11px] text-white/40 mt-1">{hint}</div>}
        </div>
    );
}

function Modal({ title, children, onClose, testId }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 rounded-md" onClick={(e) => e.stopPropagation()} data-testid={testId}>
                <div className="px-5 py-4 border-b border-white/10 flex items-center sticky top-0 bg-[#0a0a0a] z-10">
                    <div className="font-display text-lg tracking-tight">{title}</div>
                    <button onClick={onClose} className="ml-auto text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}
